import { Component, Inject, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';

import { EquipmentsService } from '@app/shared/services/entities/equipments.service';
import { UploadDownloadService } from '@app/shared/services/upload-download.service';
import { AuthenticationService } from '@app/shared/services/auth/authentication.service';
import { CustomMessageService } from '../../shared/components/custom-message/custom-message.service';
import { FileManagerDialogComponent } from '@shared/components/file-manager-dialog/file-manager-dialog.component';

/** Un registro sanitario (SSA) del equipo. */
export interface RegSsa {
  regNumber:    string | null;   // Referencia
  reg_validity: string | null;   // Vigencia (ISO YYYY-MM-DD)
  pdf:          string | null;   // Documentación (S3 key)
  reg_standar:  string | null;   // Estándar (ver REG_STANDARS)
  // Auditoría: quién subió el documento y cuándo. Se rellenan al cargar el PDF.
  uploadedBy:     string | null; // id (sub) del usuario que subió el documento
  uploadedByName: string | null; // nombre legible del usuario
  uploadedAt:     string | null; // fecha/hora ISO de la carga
  // Marca de creación del registro (para ordenar: más nuevos arriba).
  createdAt:      string | null;
}

export interface EquipmentSsaDialogData {
  equipmentId: string;
  identifier?: string;
  regSsa: RegSsa[];
}

/** Opciones del selector "Estándar". */
export const REG_STANDARS = [
  'Sometimiento de Registro SSA',
  'Registro SSA (Vigencia 5 años)',
  'Sometimiento de Prórroga con Folio',
  'Constancia de 1ra Prórroga (5-10 años)',
  'Constancia de Prórroga',
  'Registro SSA Prorrogado',
];

@Component({
  selector: 'app-equipment-ssa-dialog',
  standalone: true,
  templateUrl: './equipment-ssa-dialog.component.html',
  styleUrls: ['./equipment-ssa-dialog.component.scss'],
  imports: [CommonModule, FormsModule, MatDialogModule, MaterialModule],
})
export class EquipmentSsaDialogComponent implements OnInit {

  private equipmentsService    = inject(EquipmentsService);
  private uploadDownloadService = inject(UploadDownloadService);
  private authService          = inject(AuthenticationService);
  private customMessageService = inject(CustomMessageService);
  private dialog               = inject(MatDialog);

  readonly standards = REG_STANDARS;

  rows = signal<RegSsa[]>([]);
  saving = signal(false);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EquipmentSsaDialogData,
    public dialogRef: MatDialogRef<EquipmentSsaDialogComponent>,
  ) {}

  ngOnInit(): void {
    // Ordenamos con los más nuevos en la parte superior. Usamos createdAt si
    // existe; como respaldo para registros antiguos sin marca, la vigencia.
    const list = (this.data.regSsa ?? []).map(r => ({ ...r }));
    list.sort((a, b) => this.sortKey(b).localeCompare(this.sortKey(a)));
    this.rows.set(list);
  }

  /** Clave de orden: createdAt → uploadedAt → reg_validity → ''. */
  private sortKey(r: Partial<RegSsa>): string {
    return (r.createdAt ?? r.uploadedAt ?? r.reg_validity ?? '').toString();
  }

  add(): void {
    // Los registros nuevos se insertan ARRIBA (más nuevos en la parte superior).
    this.rows.set([
      {
        regNumber: null, reg_validity: null, pdf: null, reg_standar: null,
        uploadedBy: null, uploadedByName: null, uploadedAt: null,
        createdAt: new Date().toISOString(),
      },
      ...this.rows(),
    ]);
  }

  remove(index: number): void {
    const list = [...this.rows()];
    list.splice(index, 1);
    this.rows.set(list);
  }

  patch(index: number, key: keyof RegSsa, value: any): void {
    const list = [...this.rows()];
    list[index] = { ...list[index], [key]: value };
    this.rows.set(list);
  }

  // ─── Upload del PDF ─────────────────────────────────────────────────────
  hasPdf(index: number): boolean { return !!this.rows()[index].pdf; }

  /**
   * Abre el gestor de documentos (cargar / previsualizar / descargar / quitar)
   * para el PDF del registro indicado. Al cerrarse con "Guardar" persiste la
   * key S3 y la auditoría en la fila; con cadena vacía limpia el documento.
   */
  openFileManager(index: number): void {
    const current = this.rows()[index]?.pdf ?? '';
    const ref = this.dialog.open(FileManagerDialogComponent, {
      data: {
        value:       current,
        label:       'Documentación',
        accept:      '.pdf,application/pdf',
        uploadPath:  `equipments/${this.data.equipmentId}/reg_ssa`,
        uploadFolio: String(index),
      },
      width: '680px', maxHeight: '90vh',
    });
    ref.afterClosed().subscribe((result: string | null) => {
      if (result === null || result === undefined) return; // cancelado
      this.patch(index, 'pdf', result);
      if (result) {
        // Auditoría: registramos quién subió el documento y cuándo.
        const userId   = this.authService.getCurrentUserSub();
        const userName = (sessionStorage.getItem('userName') || this.authService.getCurrentUserEmail() || '').toString();
        this.patch(index, 'uploadedBy', userId);
        this.patch(index, 'uploadedByName', userName || null);
        this.patch(index, 'uploadedAt', new Date().toISOString());
      } else {
        // El usuario quitó el documento: limpiamos también la auditoría.
        this.patch(index, 'uploadedBy', null);
        this.patch(index, 'uploadedByName', null);
        this.patch(index, 'uploadedAt', null);
      }
    });
  }

  async viewPdf(key: string | null): Promise<void> {
    if (!key) return;
    try {
      if (/^https?:\/\//i.test(key)) { window.open(key, '_blank', 'noopener'); return; }
      const file = await this.uploadDownloadService.presign(key);
      if (file?.url) window.open(file.url, '_blank', 'noopener');
    } catch (err) {
      console.error('[regSsa] view error', err);
    }
  }

  // ─── Guardar ────────────────────────────────────────────────────────────
  private validate(): string | null {
    if (this.rows().some(r => !r.reg_standar)) return 'Todos los registros necesitan un estándar.';
    return null;
  }

  async save(): Promise<void> {
    const err = this.validate();
    if (err) {
      await this.customMessageService.open({ title: 'Revisa los datos', type: 'warning', message: err, dismissBtn: true });
      return;
    }
    this.saving.set(true);
    try {
      const resp = await this.equipmentsService.update({
        id: this.data.equipmentId,
        reg_ssa: this.rows(),
        operationType: 'single',
      } as any);
      if (resp?.state !== 'success') throw new Error(resp?.message ?? 'No se pudo guardar.');
      await this.customMessageService.open({ title: 'Éxito', type: 'success', message: 'Registros SSA guardados.', duration: 1600 });
      const updated = (resp.data?.[0]?.reg_ssa) ?? this.rows();
      this.dialogRef.close(updated);
    } catch (e: any) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: e?.message ?? 'No se pudo guardar.', dismissBtn: true });
    } finally {
      this.saving.set(false);
    }
  }

  close(): void { this.dialogRef.close(); }
}
